import debounce from 'lodash/debounce';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

type MediaBrowserDimensionsWithRefCallback = {
  width: number | null;
  height: number | null;
  refCallback: (el: HTMLDivElement | null) => void;
};

export function useMediaBrowserDimensions(): MediaBrowserDimensionsWithRefCallback {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  const refCallback = useCallback((ele: HTMLDivElement | null) => {
    ref.current = ele;

    if (ele) {
      // Set initial dimensions immediately
      setWidth(ele.clientWidth);
      setHeight(ele.clientHeight);
    }
  }, []);

  const debouncedUpdateDimensions = useMemo(
    () =>
      debounce((newWidth: number, newHeight: number) => {
        // Only update if dimensions actually changed - either panel resized or window resized
        setWidth(prevWidth => (prevWidth !== newWidth ? newWidth : prevWidth));
        setHeight(prevHeight =>
          prevHeight !== newHeight ? newHeight : prevHeight
        );
      }, 100),
    []
  );

  useEffect(() => {
    if (ref.current) {
      const newWidth = ref.current.clientWidth;
      const newHeight = ref.current.clientHeight;
      debouncedUpdateDimensions(newWidth, newHeight);
    }

    // Cleanup function to cancel any pending debounced calls
    return () => {
      debouncedUpdateDimensions.cancel();
    };
  }, [
    ref.current?.clientWidth,
    ref.current?.clientHeight,
    debouncedUpdateDimensions,
  ]);

  return {
    width,
    height,
    refCallback,
  };
}
